/**
 * React Query wrappers for the mirror-onboarding API.
 *
 * Every one of these hits an admin-gated route, so they are all opt-in through
 * `enabled` — a reviewer's Channels page must not fire a request that comes
 * back 403.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMirrorChannel,
  getMirrorOnboarding,
  listMirrorChannels,
  toggleMirrorChannel,
  MirrorCreateRequest,
} from '@/api/mirror';

export const MIRROR_CHANNELS_KEY = ['mirror-channels'];
export const MIRROR_ONBOARDING_KEY = ['mirror-onboarding'];

export const useMirrorChannels = (enabled = true) =>
  useQuery({
    queryKey: MIRROR_CHANNELS_KEY,
    queryFn: listMirrorChannels,
    enabled,
  });

// Membership and the master switches change out-of-band (a join job lands, the
// operator flips a bot_config row), so this is refetched rather than cached for
// the session — but not polled: nothing here moves minute to minute.
export const useMirrorOnboarding = (enabled = true) =>
  useQuery({
    queryKey: MIRROR_ONBOARDING_KEY,
    queryFn: getMirrorOnboarding,
    enabled,
    staleTime: 60_000,
  });

export const useCreateMirrorChannel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MirrorCreateRequest) => createMirrorChannel(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MIRROR_CHANNELS_KEY });
      qc.invalidateQueries({ queryKey: MIRROR_ONBOARDING_KEY });
    },
  });
};

export const useToggleMirrorChannel = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      backupChannelId,
      enabled,
      accountId,
    }: {
      backupChannelId: number;
      enabled: boolean;
      accountId?: number;
    }) => toggleMirrorChannel(backupChannelId, enabled, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: MIRROR_CHANNELS_KEY }),
  });
};
